<template>
  <div>
    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{locales && locales.properties && locales.properties.dependant_leave_area }}
        <small class='calculate-price__pts-hint' v-if='ds.dependant_leave_area && ds.dependant_leave_area.own_room_points_per_m2'>{{ formatPts(ds.dependant_leave_area.own_room_points_per_m2) }} pt/m²</small>
      </h2>

      <div v-for='(space, idx) in leaveArea.spaces' :key='idx' class='mb-6 pb-6 border-b border-gray-100'>
        <div class='calculate-price__form-subtitle calculate-price__room-name mb-3'>
          <template v-if='!space.is_extra'>{{ localizedName(space) }}</template>
          <template v-else>
            <select
              v-model='leaveArea.spaces[idx].facility_metadata_id'
              @change='onLeaveAreaSpaceSelect(idx)'
              class='calculate-price__room-type-select'>
              <option value=''>-- {{ locales && locales.properties && locales.properties.select_room_type }} --</option>
              <option v-for='f in (facilityOptionsByCategory && facilityOptionsByCategory.room || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
            </select>
          </template>
        </div>

        <input type='hidden' :name='`leaveArea[spaces][${idx}][name_nl]`' :value='space.name_nl || ""'/>
        <input type='hidden' :name='`leaveArea[spaces][${idx}][name_en]`' :value='space.name_en || ""'/>
        <input type='hidden' :name='`leaveArea[spaces][${idx}][wws_key]`' :value='space.wws_key || ""'/>
        <input type='hidden' :name='`leaveArea[spaces][${idx}][is_extra]`' :value='space.is_extra ? 1 : 0'/>

        <div class='mb-3'>
          <theme-radios
            :value="space.is_private == 1 ? 'private' : space.is_common == 1 ? 'common' : null"
            @input="setLeaveAreaSpaceType(idx, $event)"
            :name='`leaveArea_space_type_${idx}`'
            :options="{
              'private': `${locales && locales.properties && locales.properties.private} ${pointsLabel(ds.dependant_leave_area && ds.dependant_leave_area.own_room_points_per_m2, {suffix: locales && locales.properties && locales.properties.per_m2})}`,
              'common': `${locales && locales.properties && locales.properties.common} ${pointsLabel(ds.dependant_leave_area && ds.dependant_leave_area.common_area_points_per_m2, {suffix: (locales && locales.properties && locales.properties.per_m2) + ', ' + (locales && locales.properties && locales.properties.divided_by_residents)})}`
            }">
          </theme-radios>
          <input type='hidden' :name='`leaveArea[spaces][${idx}][is_private]`' :value='space.is_private'/>
          <input type='hidden' :name='`leaveArea[spaces][${idx}][is_common]`' :value='space.is_common'/>
        </div>

        <div v-if='space.is_common == 1' class='mb-3'>
          <theme-numeric
            v-model='leaveArea.spaces[idx].shared_addresses'
            :name='`leaveArea[spaces][${idx}][shared_addresses]`'
            :label='locales && locales.properties && locales.properties.common_space_residents'
            @input='countDependantLeaveAreaPoints'>
          </theme-numeric>
        </div>

        <div class='grid grid-cols-3 gap-x-2 sm:gap-x-5 gap-y-5 items-end mb-3'>
          <div>
            <theme-numeric
              v-model='leaveArea.spaces[idx].length'
              :name='`leaveArea[spaces][${idx}][length]`'
              :label='locales && locales.properties && locales.properties.room_length || "Lengte (m)"'
              @input='calculateLeaveAreaSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='leaveArea.spaces[idx].width'
              :name='`leaveArea[spaces][${idx}][width]`'
              :label='locales && locales.properties && locales.properties.room_width || "Breedte (m)"'
              @input='calculateLeaveAreaSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='leaveArea.spaces[idx].surface'
              :name='`leaveArea[spaces][${idx}][surface]`'
              :label='locales && locales.properties && locales.properties.room_surface || "Oppervlak"'
              @input='countDependantLeaveAreaPoints'
              :IsExtraInfo='true'
              :extra-info-suffix='true'>
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
        </div>
        <div
          v-for='(dim, dIdx) in (leaveArea.spaces[idx].extra_dimensions || [])'
          :key='"leave_extra_dim_" + idx + "_" + dIdx'
          class='grid grid-cols-3 gap-x-2 sm:gap-x-5 gap-y-5 items-end mb-3'>
          <div>
            <theme-numeric
              v-model='dim.length'
              :name='`leaveArea[spaces][${idx}][extra_dimensions][${dIdx}][length]`'
              :label='locales && locales.properties && locales.properties.room_length || "Lengte (m)"'
              @input='calculateLeaveAreaSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='dim.width'
              :name='`leaveArea[spaces][${idx}][extra_dimensions][${dIdx}][width]`'
              :label='locales && locales.properties && locales.properties.room_width || "Breedte (m)"'
              @input='calculateLeaveAreaSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <button type='button' class='calculate-price__remove-dim-btn' @click='removeLeaveAreaExtraDimension(idx, dIdx)'>
              {{ locales && locales.properties && locales.properties.remove_dimension_row || 'Verwijderen' }}
            </button>
          </div>
        </div>
        <div class='mb-3'>
          <button type='button' class='calculate-price__add-room-btn' @click='addLeaveAreaExtraDimension(idx)'>
            + {{ locales && locales.properties && locales.properties.add_dimension_row || 'Lengte en breedte veld toevoegen' }}
          </button>
        </div>
        <div class='flex flex-wrap items-center gap-6'>
          <div>
            <input type='hidden' :name='`leaveArea[spaces][${idx}][heated]`' value='0'/>
            <theme-checkboxes
              :id='`leaveArea_heated_${idx}`'
              @input='countHeatingPoints'
              label=''
              :is-single-checkbox='true'
              :name='`leaveArea[spaces][${idx}][heated]`'
              v-model='leaveArea.spaces[idx].heated'
              :options="{ '1': `${(locales && locales.properties && locales.properties.heated) || 'Heated'} ${leaveArea.spaces[idx].is_common == 1 ? pointsLabel(ds.dependant_heating && ds.dependant_heating.common_room, {suffix: locales && locales.properties && locales.properties.divided_by_addresses}) : pointsLabel(ds.dependant_heating && ds.dependant_heating.own_room)}` }">
            </theme-checkboxes>
            <template v-if='leaveArea.spaces[idx].heated == 1'>
              <input type='hidden' :name='`leaveArea[spaces][${idx}][heated]`' value='1'/>
            </template>
          </div>
          <div>
            <input type='hidden' :name='`leaveArea[spaces][${idx}][cooled]`' value='0'/>
            <theme-checkboxes
              :id='`leaveArea_cooled_${idx}`'
              @input='countHeatingPoints'
              label=''
              :is-single-checkbox='true'
              :name='`leaveArea[spaces][${idx}][cooled]`'
              v-model='leaveArea.spaces[idx].cooled'
              :options="{ '1': `${(locales && locales.properties && locales.properties.cooled) || 'Cooled'} ${leaveArea.spaces[idx].is_common == 1 ? pointsLabel(ds.dependant_heating && ds.dependant_heating.common_room_cooling, {suffix: locales && locales.properties && locales.properties.divided_by_addresses}) : pointsLabel(ds.dependant_heating && ds.dependant_heating.own_room_cooling)}` }">
            </theme-checkboxes>
            <template v-if='leaveArea.spaces[idx].cooled == 1'>
              <input type='hidden' :name='`leaveArea[spaces][${idx}][cooled]`' value='1'/>
            </template>
          </div>
        </div>
      </div>

      <div class='mb-5'>
        <button type='button' @click='addLeaveAreaSpace' class='calculate-price__add-room-btn'>
          + {{ locales && locales.properties && locales.properties.add_room || 'Add space' }}
        </button>
      </div>

      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='leaveArea.total'
            name='leaveArea[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.leave_area)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{locales && locales.properties && locales.properties.dependant_other_spaces_area }}
        <small class='calculate-price__pts-hint' v-if='ds.dependant_other_spaces_area && ds.dependant_other_spaces_area.private_area_points_per_m2'>{{ formatPts(ds.dependant_other_spaces_area.private_area_points_per_m2) }} pt/m²</small>
      </h2>

      <div v-for='(space, idx) in otherSpaces.spaces' :key='idx' class='mb-6 pb-6 border-b border-gray-100'>
        <div class='calculate-price__form-subtitle calculate-price__room-name mb-3'>
          <template v-if='!space.is_extra'>{{ localizedName(space) }}</template>
          <template v-else>
            <select
              v-model='otherSpaces.spaces[idx].facility_metadata_id'
              @change='onOtherSpaceSelect(idx)'
              class='calculate-price__room-type-select'>
              <option value=''>-- {{ locales && locales.properties && locales.properties.select_room_type }} --</option>
              <option v-for='f in (facilityOptionsByCategory && facilityOptionsByCategory.other_room || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
            </select>
          </template>
        </div>

        <input type='hidden' :name='`otherSpaces[spaces][${idx}][name_nl]`' :value='space.name_nl || ""'/>
        <input type='hidden' :name='`otherSpaces[spaces][${idx}][name_en]`' :value='space.name_en || ""'/>
        <input type='hidden' :name='`otherSpaces[spaces][${idx}][wws_key]`' :value='space.wws_key || ""'/>
        <input type='hidden' :name='`otherSpaces[spaces][${idx}][is_extra]`' :value='space.is_extra ? 1 : 0'/>

        <div class='mb-3'>
          <theme-radios
            :value="space.is_private == 1 ? 'private' : space.is_common == 1 ? 'common' : null"
            @input="setOtherSpaceType(idx, $event)"
            :name='`otherSpaces_space_type_${idx}`'
            :options="{
              'private': `${locales && locales.properties && locales.properties.private} ${pointsLabel(ds.dependant_other_spaces_area && ds.dependant_other_spaces_area.private_area_points_per_m2, {suffix: locales && locales.properties && locales.properties.per_m2})}`,
              'common': `${locales && locales.properties && locales.properties.common} ${pointsLabel(ds.dependant_other_spaces_area && ds.dependant_other_spaces_area.common_area_points_per_m2, {suffix: (locales && locales.properties && locales.properties.per_m2) + ', ' + (locales && locales.properties && locales.properties.divided_by_residents)})}`
            }">
          </theme-radios>
          <input type='hidden' :name='`otherSpaces[spaces][${idx}][is_private]`' :value='space.is_private'/>
          <input type='hidden' :name='`otherSpaces[spaces][${idx}][is_common]`' :value='space.is_common'/>
        </div>

        <div v-if='space.is_common == 1' class='mb-3'>
          <theme-numeric
            v-model='otherSpaces.spaces[idx].shared_addresses'
            :name='`otherSpaces[spaces][${idx}][shared_addresses]`'
            :label='locales && locales.properties && locales.properties.common_space_residents'
            @input='countDependantOtherSpacesAreaPoints'>
          </theme-numeric>
        </div>

        <div class='grid grid-cols-3 gap-x-2 sm:gap-x-5 gap-y-5 items-end mb-3'>
          <div>
            <theme-numeric
              v-model='otherSpaces.spaces[idx].length'
              :name='`otherSpaces[spaces][${idx}][length]`'
              :label='locales && locales.properties && locales.properties.room_length || "Lengte (m)"'
              @input='calculateOtherSpaceSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='otherSpaces.spaces[idx].width'
              :name='`otherSpaces[spaces][${idx}][width]`'
              :label='locales && locales.properties && locales.properties.room_width || "Breedte (m)"'
              @input='calculateOtherSpaceSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='otherSpaces.spaces[idx].surface'
              :name='`otherSpaces[spaces][${idx}][surface]`'
              :label='locales && locales.properties && locales.properties.room_surface || "Oppervlak"'
              @input='countDependantOtherSpacesAreaPoints'
              :IsExtraInfo='true'
              :extra-info-suffix='true'>
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
        </div>
        <div
          v-for='(dim, dIdx) in (otherSpaces.spaces[idx].extra_dimensions || [])'
          :key='"other_extra_dim_" + idx + "_" + dIdx'
          class='grid grid-cols-3 gap-x-2 sm:gap-x-5 gap-y-5 items-end mb-3'>
          <div>
            <theme-numeric
              v-model='dim.length'
              :name='`otherSpaces[spaces][${idx}][extra_dimensions][${dIdx}][length]`'
              :label='locales && locales.properties && locales.properties.room_length || "Lengte (m)"'
              @input='calculateOtherSpaceSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='dim.width'
              :name='`otherSpaces[spaces][${idx}][extra_dimensions][${dIdx}][width]`'
              :label='locales && locales.properties && locales.properties.room_width || "Breedte (m)"'
              @input='calculateOtherSpaceSurface(idx)'>
            </theme-numeric>
          </div>
          <div>
            <button type='button' class='calculate-price__remove-dim-btn' @click='removeOtherSpaceExtraDimension(idx, dIdx)'>
              {{ locales && locales.properties && locales.properties.remove_dimension_row || 'Verwijderen' }}
            </button>
          </div>
        </div>
        <div class='mb-3'>
          <button type='button' class='calculate-price__add-room-btn' @click='addOtherSpaceExtraDimension(idx)'>
            + {{ locales && locales.properties && locales.properties.add_dimension_row || 'Lengte en breedte veld toevoegen' }}
          </button>
        </div>
        <div class='grid grid-cols-2 gap-x-5 gap-y-5 items-end'>
          <div>
            <input type='hidden' :name='`otherSpaces[spaces][${idx}][heated]`' value='0'/>
            <theme-checkboxes
              :id='`otherSpaces_heated_${idx}`'
              @input='countHeatingPoints'
              label=''
              :is-single-checkbox='true'
              :name='`otherSpaces[spaces][${idx}][heated]`'
              v-model='otherSpaces.spaces[idx].heated'
              :options="{ '1': `${(locales && locales.properties && locales.properties.heated) || 'Heated'} ${otherSpaces.spaces[idx].is_common == 1 ? pointsLabel(ds.dependant_heating && ds.dependant_heating.other_common_room, {suffix: locales && locales.properties && locales.properties.divided_by_addresses}) : pointsLabel(ds.dependant_heating && ds.dependant_heating.other_own_room)}` }">
            </theme-checkboxes>
            <template v-if='otherSpaces.spaces[idx].heated == 1'>
              <input type='hidden' :name='`otherSpaces[spaces][${idx}][heated]`' value='1'/>
            </template>
          </div>
        </div>
      </div>

      <div class='mb-5'>
        <button type='button' @click='addOtherSpace' class='calculate-price__add-room-btn'>
          + {{ locales && locales.properties && locales.properties.add_room || 'Add space' }}
        </button>
      </div>

      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='otherSpaces.total'
            name='otherSpaces[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.other_spaces)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.heating_and_cooling }}
        <small class='calculate-price__pts-hint'>tot {{ formatPts(maxHeatingPts) }} pt</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='heating.total'
            name='heating[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.heated_private_rooms)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.energy_performance }}
      </h2>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.energy_label }}
          <strong v-if="energy.energy_label && energyLabels[energy.energy_label] && energyLabels[energy.energy_label]['slug'] != 'no_label_or_index'">
            {{ energy.energy_points || '' }}
          </strong>
        </h3>
        <div class='mb-10'>
          <theme-radios
            v-model='energy.energy_label'
            name='energy[energy_label]'
            :options='energyLabelsOptions'
            :sort='true'
            sort-by='custom'
            :custom-order='energyLabelsCustomOrderWithPoints'
            @input='countEnergyPoints'>
          </theme-radios>
        </div>
        <div v-if="energy.energy_label && energyLabels[energy.energy_label] && energyLabels[energy.energy_label]['slug'] == 'no_label_or_index'" class='grid grid-cols-1 lg:grid-cols-12'>
          <div class='col-span-4'>
            <theme-input
              @input='countEnergyPoints'
              :label="`${locales &&locales.properties && locales.properties.construction_year} ${energy.building_construction_year && energy.energy_points || ''}`"
              v-model='energy.building_construction_year'
              name='energy[building_construction_year]'>
            </theme-input>
          </div>
        </div>
      </div>
      <div class='mb-5'>
        <input type='hidden' name='energy[monument_allowance]' value='0'/>
        <theme-checkboxes
          id='monumentAllowance'
          @input='countEnergyPoints'
          label=''
          :is-single-checkbox='true'
          name='energy[monument_allowance]'
          v-model='energy.monument_allowance'
          :options="{ '1': `${locales && locales.properties && locales.properties.monument_allowance_energy}` }">
        </theme-checkboxes>
        <template v-if='energy.monument_allowance == 1'>
          <input type='hidden' name='energy[monument_allowance]' value='1'/>
        </template>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='energy.total'
            valid-scope='modal-form'
            name='energy[total]'
            :label='locales && locales.properties && locales.properties.total_energy'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.kitchen }}
        <small class='calculate-price__pts-hint' v-if='maxKitchenPts'>aanrecht tot {{ formatPts(maxKitchenPts) }} pt</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10'>
        <div>
          <theme-numeric
            v-model="kitchen['number_of_residents']"
            name='kitchen[number_of_residents]'
            :label="`${locales && locales.properties && locales.properties.number_of_residents}`"
            @input='countKitchenPoints'
            :IsExtraInfo='true'
          >
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10'>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.worktop_length }}
            <strong v-if='kitchen.worktop_length.value'>{{ kitchen.worktop_length.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.worktop_length.value'
            name='kitchen[worktop_length]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.less_than_1_meter} ${pointsLabel(0)}`,
              '2': `${locales && locales.properties && locales.properties['1_2_meters']} ${pointsLabel(ds.kitchen && ds.kitchen.worktop_1_2m_points)}`,
              '3': `${locales && locales.properties && locales.properties['2_3_meters']} ${pointsLabel(ds.kitchen && ds.kitchen.worktop_2_3m_points)}`,
              '4': `${locales && locales.properties && locales.properties.more_than_3_meters} ${pointsLabel(ds.kitchen && ds.kitchen.worktop_3m_points)}`,
              '5': `${locales && locales.properties && locales.properties.more_than_5_meters} ${pointsLabel(ds.kitchen && ds.kitchen.worktop_5m_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.cooker }}
            <strong v-if='kitchen.cooker.value'>{{ kitchen.cooker.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.cooker.value'
            name='kitchen[cooker]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.built_in_gas_hob} ${pointsLabel(ds.kitchen && ds.kitchen.gas_cooker_points)}`,
              '2': `${locales && locales.properties && locales.properties.built_in_ceramic_hob} ${pointsLabel(ds.kitchen && ds.kitchen.ceramic_cooker_points)}`,
              '3': `${locales && locales.properties && locales.properties.built_in_induction_hob} ${pointsLabel(ds.kitchen && ds.kitchen.induction_cooker_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.oven }}
            <strong v-if='kitchen.oven.value'>{{ kitchen.oven.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.oven.value'
            name='kitchen[oven]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.built_in_gas_oven} ${pointsLabel(ds.kitchen && ds.kitchen.gas_oven_points)}`,
              '2': `${locales && locales.properties && locales.properties.built_in_electric_oven} ${pointsLabel(ds.kitchen && ds.kitchen.electric_oven_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
      </div>
      <div class='mb-10'>
        <div class='grid grid-cols-1 lg:grid-cols-5 gap-5'>
          <div>
            <input type='hidden' name='kitchen[built_in_microwave]' value='0'/>
            <theme-checkboxes
              id='built_in_microwave'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_microwave]'
              v-model='kitchen.built_in_microwave.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_microwave} ${pointsLabel(kitchen.built_in_microwave.points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_extractor_hood]' value='0'/>
            <theme-checkboxes
              id='built_in_extractor_hood'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_extractor_hood]'
              v-model='kitchen.built_in_extractor_hood.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_extractor_hood} ${pointsLabel(kitchen.built_in_extractor_hood.points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_fridge]' value='0'/>
            <theme-checkboxes
              id='built_in_fridge'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_fridge]'
              v-model='kitchen.built_in_fridge.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_fridge} ${pointsLabel(kitchen.built_in_fridge.points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_freezer]' value='0'/>
            <theme-checkboxes
              id='built_in_freezer'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_freezer]'
              v-model='kitchen.built_in_freezer.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_freezer} ${pointsLabel(kitchen.built_in_freezer.points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_dishwasher]' value='0'/>
            <theme-checkboxes
              id='built_in_dishwasher'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_dishwasher]'
              v-model='kitchen.built_in_dishwasher.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_dishwasher} ${pointsLabel(kitchen.built_in_dishwasher.points)}` }">
            </theme-checkboxes>
          </div>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='kitchen.luxury_mixer_tap.value'
            valid-scope='modal-form'
            name='kitchen[luxury_mixer_tap]'
            :label="`${locales && locales.properties && locales.properties.luxury_mixer_tap} ${pointsLabel(kitchen.luxury_mixer_tap.points)} ${kitchen.luxury_mixer_tap.value ? kitchen.luxury_mixer_tap.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='kitchen.thermostatic_water_mixer.value'
            valid-scope='modal-form'
            name='kitchen[thermostatic_water_mixer]'
            :label="`${locales && locales.properties && locales.properties.thermostatic_water_mixer} ${pointsLabel(kitchen.thermostatic_water_mixer.points)} ${kitchen.thermostatic_water_mixer.value ? kitchen.thermostatic_water_mixer.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='kitchen.boiling_water_function.value'
            valid-scope='modal-form'
            name='kitchen[boiling_water_function]'
            :label="`${locales && locales.properties && locales.properties.boiling_water_function} ${pointsLabel(kitchen.boiling_water_function.points)} ${kitchen.boiling_water_function.value ? kitchen.boiling_water_function.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='kitchen.extra_single_cupboard.value'
            valid-scope='modal-form'
            name='kitchen[extra_single_cupboard]'
            :label="`${locales && locales.properties && locales.properties.extra_single_cupboard} ${pointsLabel(kitchen.extra_single_cupboard.points)} ${kitchen.extra_single_cupboard.value ? kitchen.extra_single_cupboard.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='kitchen.total'
            valid-scope='modal-form'
            name='kitchen[total]'
            :label='locales && locales.properties && locales.properties.total_kitchen'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.sanitary }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10'>
        <div>
          <theme-numeric
            v-model="sanitary['number_of_residents']"
            name='sanitary[number_of_residents]'
            :label="`${locales && locales.properties && locales.properties.number_of_residents_sanitary}`"
            @input='countSanitaryPoints'
            :IsExtraInfo='true'
          >
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_toilets} ${sanitary.number_of_toilets.value && sanitary.number_of_toilets.total || ''}`"
            v-model='sanitary.number_of_toilets.value'
            name='sanitary[number_of_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_hunged_toilets} ${sanitary.number_of_hunged_toilets.value && sanitary.number_of_hunged_toilets.total || ''}`"
            v-model='sanitary.number_of_hunged_toilets.value'
            name='sanitary[number_of_hunged_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_separate_toilets} ${sanitary.number_of_separate_toilets.value && sanitary.number_of_separate_toilets.total || ''}`"
            v-model='sanitary.number_of_separate_toilets.value'
            name='sanitary[number_of_separate_toilets]'>
          </theme-input>
        </div>
        <div>
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_own_toilets} ${sanitary.number_of_own_toilets.value && sanitary.number_of_own_toilets.total || ''}`"
            v-model='sanitary.number_of_own_toilets.value'
            name='sanitary[number_of_own_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_own_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_own_hunged_toilets} ${sanitary.number_of_own_hunged_toilets.value && sanitary.number_of_own_hunged_toilets.total || ''}`"
            v-model='sanitary.number_of_own_hunged_toilets.value'
            name='sanitary[number_of_own_hunged_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_own_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_own_separate_toilets} ${sanitary.number_of_own_separate_toilets.value && sanitary.number_of_own_separate_toilets.total || ''}`"
            v-model='sanitary.number_of_own_separate_toilets.value'
            name='sanitary[number_of_own_separate_toilets]'>
          </theme-input>
        </div>
        <div>
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_washbasins} ${sanitary.number_of_washbasins.value && sanitary.number_of_washbasins.total || ''}`"
            v-model='sanitary.number_of_washbasins.value'
            name='sanitary[number_of_washbasins]'>
          </theme-input>
        </div>
      </div>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.own_bathroom }}
        </h3>
        <theme-radios
          @input='countSanitaryPoints'
          label=''
          name='sanitary[own_bathroom]'
          v-model='sanitary.own_bathroom'
          :options="{
              '0':
                locales &&
                locales.common &&
                locales.common.no,
              '1':
                locales &&
                locales.common &&
                locales.common.yes
            }">
        </theme-radios>
      </div>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.sanitary }}
          <strong v-if='sanitary.shower_bath.value'>{{ sanitary.shower_bath.total || '' }}</strong>
        </h3>
        <theme-radios
          @input='countSanitaryPoints'
          label=''
          name='sanitary[shower_bath]'
          v-model='sanitary.shower_bath.value'
          :options="{
              '0': `${locales && locales.properties && locales.properties.no_shower_or_bath} ${pointsLabel(0)}`,
              '1': `${locales && locales.properties && locales.properties.shower_available} ${pointsLabel(ds.sanitary && ds.sanitary.shower_points)}`,
              '2': `${locales && locales.properties && locales.properties.bath_available} ${pointsLabel(ds.sanitary && ds.sanitary.bath_points)}`,
              '3': `${locales && locales.properties && locales.properties.separate_bath_and_shower_in_one_room} ${pointsLabel(ds.sanitary && ds.sanitary.shower_bath_points)}`,
            }">
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10'>
        <div>
          <input type='hidden' name='sanitary[cupboard_with_built_in_washbasin]' value='0'/>
          <theme-checkboxes
            id='cupboard_with_built_in_washbasin'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[cupboard_with_built_in_washbasin]'
            v-model='sanitary.cupboard_with_built_in_washbasin.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.cupboard_with_built_in_washbasin} ${pointsLabel(sanitary.cupboard_with_built_in_washbasin.points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[extra_for_a_bubble_bath]' value='0'/>
          <theme-checkboxes
            id='extra_for_a_bubble_bath'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[extra_for_a_bubble_bath]'
            v-model='sanitary.extra_for_a_bubble_bath.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.extra_for_a_bubble_bath} ${pointsLabel(sanitary.extra_for_a_bubble_bath.points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[mounted_complete_separation_of_shower]' value='0'/>
          <theme-checkboxes
            id='mounted_complete_separation_of_shower'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[mounted_complete_separation_of_shower]'
            v-model='sanitary.mounted_complete_separation_of_shower.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.mounted_complete_separation_of_shower} ${pointsLabel(sanitary.mounted_complete_separation_of_shower.points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[towel_radiator]' value='0'/>
          <theme-checkboxes
            id='towel_radiator'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[towel_radiator]'
            v-model='sanitary.towel_radiator.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.towel_radiator} ${pointsLabel(sanitary.towel_radiator.points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[power_outlet]' value='0'/>
          <theme-checkboxes
            id='power_outlet'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[power_outlet]'
            v-model='sanitary.power_outlet.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.power_outlet} ${pointsLabel(ds.sanitary && ds.sanitary.power_outlet_points)}` }">
          </theme-checkboxes>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='sanitary.luxury_mixer_tap.value'
            valid-scope='modal-form'
            name='sanitary[luxury_mixer_tap]'
            :label="`${locales && locales.properties && locales.properties.luxury_mixer_tap} ${pointsLabel(sanitary.luxury_mixer_tap.points)} ${sanitary.luxury_mixer_tap.value ? sanitary.luxury_mixer_tap.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='sanitary.thermostatic_water_mixer.value'
            valid-scope='modal-form'
            name='sanitary[thermostatic_water_mixer]'
            :label="`${locales && locales.properties && locales.properties.thermostatic_water_mixer} ${pointsLabel(sanitary.thermostatic_water_mixer.points)} ${sanitary.thermostatic_water_mixer.value ? sanitary.thermostatic_water_mixer.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
        <div v-if="sanitary.power_outlet.value > 0">
          <theme-numeric
            v-model='sanitary.number_of_power_outlet.value'
            valid-scope='modal-form'
            name='sanitary[number_of_power_outlet]'
            :label="`${locales && locales.properties && locales.properties.number_of_power_outlet} ${sanitary.number_of_power_outlet.value ? sanitary.power_outlet.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='sanitary.total'
            valid-scope='modal-form'
            name='sanitary[total]'
            :label='locales && locales.properties && locales.properties.total_sanitary'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.housing_for_disabled }}
        <small class='calculate-price__pts-hint' v-if='ds.disabled && ds.disabled.total_investment_points'>{{ formatPts(ds.disabled.total_investment_points) }} pt / €1000</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='disabled.total_investment.value'
            valid-scope='modal-form'
            name='disabled[total_investment]'
            :label='locales && locales.properties && locales.properties.disabled_total_investment'
            @input='countDisabledPoints'
            :precision='2'>
          </theme-numeric>
        </div>
        <div>
          <div>
            <theme-numeric
              v-model="disabled['number_of_residents']"
              name='disabled[number_of_residents]'
              :label="`${locales && locales.properties && locales.properties.number_of_residents}`"
              @input='countDisabledPoints'
              :IsExtraInfo='true'
            >
            </theme-numeric>
          </div>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='disabled.total'
            valid-scope='modal-form'
            name='disabled[total]'
            :label='locales && locales.properties && locales.properties.total_housing_for_disabled'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.private_outdoor_space }}
        <small class='calculate-price__pts-hint' v-if='ds.outdoor && ds.outdoor.max_points'>tot {{ formatPts(ds.outdoor.max_points) }} pt · {{ formatPts(ds.outdoor.space_points) }} pt/m²</small>
      </h2>

      <div v-for='(space, idx) in outdoor.spaces' :key='idx' class='mb-6 pb-6 border-b border-gray-100'>
        <div class='calculate-price__form-subtitle calculate-price__room-name mb-3'>
          <template v-if='!space.is_extra'>{{ localizedName(space) }}</template>
          <template v-else>
            <select
              v-model='outdoor.spaces[idx].facility_metadata_id'
              @change='onOutdoorSpaceSelect(idx)'
              class='calculate-price__room-type-select'>
              <option value=''>-- {{ locales && locales.properties && locales.properties.select_outdoor_space_type }} --</option>
              <option v-for='f in (facilityOptionsByCategory && facilityOptionsByCategory.outdoor_space || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
            </select>
          </template>
        </div>

        <input type='hidden' :name='`outdoor[spaces][${idx}][name_nl]`' :value='space.name_nl || ""'/>
        <input type='hidden' :name='`outdoor[spaces][${idx}][name_en]`' :value='space.name_en || ""'/>
        <input type='hidden' :name='`outdoor[spaces][${idx}][is_extra]`' :value='space.is_extra ? 1 : 0'/>

        <div class='mb-3'>
          <theme-radios
            :value="space.is_private == 1 ? 'private' : space.is_common == 1 ? 'common' : null"
            @input="setOutdoorSpaceType(idx, $event)"
            :name='`outdoor_space_type_${idx}`'
            :options="{
              'private': `${locales && locales.properties && locales.properties.private} (${pointsLabel(ds.outdoor && ds.outdoor.space_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false})}, ${pointsLabel(ds.outdoor && ds.outdoor.min_space_points, {wrap: false})} ${(locales && locales.properties && locales.properties.outdoor_bonus_once_note) || 'once, not per space'})`,
              'common': `${locales && locales.properties && locales.properties.common} (${pointsLabel(ds.outdoor && ds.outdoor.common_space_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false})}, ${(locales && locales.properties && locales.properties.divided_by_addresses) || 'divided by addresses'})`
            }">
          </theme-radios>
          <input type='hidden' :name='`outdoor[spaces][${idx}][is_private]`' :value='space.is_private'/>
          <input type='hidden' :name='`outdoor[spaces][${idx}][is_common]`' :value='space.is_common'/>
        </div>

        <div v-if='space.is_common == 1' class='grid grid-cols-1 lg:grid-cols-2 gap-x-5 mb-3'>
          <div>
            <theme-numeric
              v-model='outdoor.spaces[idx].residents'
              :name='`outdoor[spaces][${idx}][residents]`'
              :label='locales && locales.properties && locales.properties.common_space_residents'
              @input='countOutdoorPoints'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='outdoor.spaces[idx].shared_addresses'
              :name='`outdoor[spaces][${idx}][shared_addresses]`'
              :label='locales && locales.properties && locales.properties.number_of_addresses'
              @input='countOutdoorPoints'>
            </theme-numeric>
          </div>
        </div>

        <div class='grid grid-cols-3 gap-x-2 sm:gap-x-5 gap-y-5 items-end'>
          <div>
            <theme-numeric
              v-model='outdoor.spaces[idx].length'
              :name='`outdoor[spaces][${idx}][length]`'
              :label='locales && locales.properties && locales.properties.room_length || "Lengte (m)"'
              @input='calculateOutdoorSpaceArea(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='outdoor.spaces[idx].width'
              :name='`outdoor[spaces][${idx}][width]`'
              :label='locales && locales.properties && locales.properties.room_width || "Breedte (m)"'
              @input='calculateOutdoorSpaceArea(idx)'>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='outdoor.spaces[idx].area'
              :name='`outdoor[spaces][${idx}][area]`'
              :label='locales && locales.properties && locales.properties.room_surface || "Oppervlak"'
              :precision='2'
              @input='countOutdoorPoints'
              :IsExtraInfo='true'
              :extra-info-suffix='true'>
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
        </div>
      </div>

      <div class='mb-5'>
        <button type='button' @click='addOutdoorSpace' class='calculate-price__add-room-btn'>
          + {{ locales && locales.properties && locales.properties.add_room || 'Add space' }}
        </button>
      </div>

      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='outdoor.total'
            name='outdoor[total]'
            :label='locales && locales.properties && locales.properties.total_outdoor'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.shared_indoor_space }}
        <small class='calculate-price__pts-hint'>{{ pointsLabel(ds.common_indoor_shared_with_multiple_addresses && ds.common_indoor_shared_with_multiple_addresses.common_departure_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false}) }} / {{ pointsLabel(ds.common_indoor_shared_with_multiple_addresses && ds.common_indoor_shared_with_multiple_addresses.common_other_space_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false}) }} · {{ locales && locales.properties && locales.properties.divided_by_addresses }}</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='indoor.common_departure.value'
            valid-scope='modal-form'
            name='indoor[common_departure]'
            :label="`${locales && locales.properties && locales.properties.common_departure} ${pointsLabel(ds.common_indoor_shared_with_multiple_addresses && ds.common_indoor_shared_with_multiple_addresses.common_departure_points, {suffix: locales && locales.properties && locales.properties.per_m2})}`"
            :precision='2'
            @input='countIndoorPoints'>
          </theme-numeric>
        </div>
        <div>
            <theme-numeric
              v-model="indoor.common_departure.number_of_addresses"
              name='indoor[number_of_addresses]'
              :label="`${locales && locales.properties && locales.properties.common_indoor_space_addresses}`"
              @input='countIndoorPoints'
              :IsExtraInfo='true'
            >
            </theme-numeric>
          </div>
        <div>
          <theme-numeric
            v-model="indoor.common_departure.units_per_address"
            name='indoor[common_departure_units_per_address]'
            :label="`${locales && locales.properties && locales.properties.indoor_units_per_address || 'Wooneenheden per adres'}`"
            :precision='0'
            @input='countIndoorPoints'
            :IsExtraInfo='true'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='indoor.common_other_space.value'
            valid-scope='modal-form'
            name='indoor[common_other_space]'
            :label="`${locales && locales.properties && locales.properties.common_other_space} ${pointsLabel(ds.common_indoor_shared_with_multiple_addresses && ds.common_indoor_shared_with_multiple_addresses.common_other_space_points, {suffix: locales && locales.properties && locales.properties.per_m2})}`"
            :precision='2'
            @input='countIndoorPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='indoor.common_other_space.common_other_space_addresses'
            valid-scope='modal-form'
            name='indoor[common_other_space_addresses]'
            :label="`${locales && locales.properties && locales.properties.common_other_space_addresses}`"
            :precision='0'
            @input='countIndoorPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model="indoor.common_other_space.units_per_address"
            name='indoor[common_other_space_units_per_address]'
            :label="`${locales && locales.properties && locales.properties.indoor_units_per_address || 'Wooneenheden per adres'}`"
            :precision='0'
            @input='countIndoorPoints'
            :IsExtraInfo='true'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='indoor.total'
            valid-scope='modal-form'
            name='indoor[total]'
            :label='locales && locales.properties && locales.properties.total_indoor'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.shared_parking }}
        <small class='calculate-price__pts-hint' v-if='maxParkingPts'>tot {{ formatPts(maxParkingPts) }} pt · {{ locales && locales.properties && locales.properties.divided_by_addresses }}</small>
      </h2>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.shared_parking }}
        </h3>
        <theme-radios
          v-model='sharedParking.type'
          name='shared_parking[type]'
          :options='sharedParkingOptionsWithPoints'
          @input='countSharedParkingPoints'>
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10'>
        <div>
          <theme-numeric
            v-model='sharedParking.number_of_residents'
            valid-scope='modal-form'
            name='shared_parking[number_of_residents]'
            :label='locales && locales.properties && locales.properties.parking_number_of_residents'
            @input='countSharedParkingPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='sharedParking.number_of_addresses'
            valid-scope='modal-form'
            name='shared_parking[number_of_addresses]'
            :label='locales && locales.properties && locales.properties.number_of_addresses'
            @input='countSharedParkingPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='sharedParking.total'
            valid-scope='modal-form'
            name='shared_parking[total]'
            :label='locales && locales.properties && locales.properties.total_shared_parking'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.vop }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10 items-end'>
        <div>
          <a target="_blank" href="https://www.wozwaardeloket.nl">{{locales && locales.common && locales.common.check_here}}</a>
          <theme-numeric
            v-model='vop.price.value'
            valid-scope='modal-form'
            name='vop[price]'
            :label='locales && locales.properties && locales.properties.enter_vop'
            @input='countVopPoints'>
          </theme-numeric>
        </div>
      
        <div>
          <a target="_blank" :href="dependantSettings.price_points_settings.average_vop_prices_link">{{locales && locales.common && locales.common.check_here}}</a>
          <theme-numeric
            v-model='vop.average_price'
            valid-scope='modal-form'
            name='vop[average_price]'
            :label='locales && locales.properties && locales.properties.average_price_per_m2'
            @input='countVopPoints'>
          </theme-numeric>
        </div>
      
        <div>
          <theme-numeric
            v-model='vop.surface'
            valid-scope='modal-form'
            name='vop[surface]'
            :label='locales && locales.properties && locales.properties.surface'
            @input='countVopPoints'
            :IsExtraInfo='true'
            :extra-info-suffix='true'>
            <template slot='extra-info'> m<sup>2</sup></template>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='vop.valuation.value'
            valid-scope='modal-form'
            name='vop[valuation]'
            :label='locales && locales.properties && locales.properties.vop_accredited_valuation'
            @input='countVopPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='vop.woz_reference_year'
            valid-scope='modal-form'
            name='vop[woz_reference_year]'
            :label='locales && locales.properties && locales.properties.vop_reference_year'>
          </theme-numeric>
        </div>
        <div>
          <theme-input
            v-model='vop.corop_region'
            valid-scope='modal-form'
            name='vop[corop_region]'
            :label='locales && locales.properties && locales.properties.vop_corop_region'>
          </theme-input>
        </div>
      </div>
      <div class='calculate-price__formula-box mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.vop_formula_title }}
        </h3>
        <p class='calculate-price__pts-hint'>
          {{ formatPts(ds.vop && ds.vop.lower_than_10) }} / {{ formatPts(ds.vop && ds.vop.not_lower_than_10_and_not_higher_than_10) }} / {{ formatPts(ds.vop && ds.vop.higher_than_10) }} {{ locales && locales.properties && locales.properties.woz_tiered_depending_on_region }}
        </p>
        <template v-if='vop.hasData'>
          <p>
            {{ locales && locales.properties && locales.properties.vop_formula_price_per_m2 }} =
            € {{ formatPts(vop.priceUsed) }} / {{ vop.surfaceUsed }} m² = € {{ formatPts(vop.pointsPerSurface) }}/m²
          </p>
          <p>
            {{ locales && locales.properties && locales.properties.average_price_per_m2 }}: € {{ formatPts(vop.averagePriceUsed) }}
          </p>
          <p>
            {{ locales && locales.properties && locales.properties.vop_formula_difference_percent }}:
            {{ vop.differencePercent > 0 ? '+' : '' }}{{ formatPts(vop.differencePercent) }}%
            ({{ locales && locales.properties && locales.properties['vop_formula_category_' + vop.category] }})
          </p>
        </template>
        <template v-else>
          <p>{{ locales && locales.properties && locales.properties.vop_formula_no_data }}</p>
        </template>
        <p v-if='vop.fallbackUsed === "valuation"'>
          {{ locales && locales.properties && locales.properties.vop_formula_fallback_valuation_used }}
        </p>
        <p v-else-if='vop.fallbackUsed === "none"'>
          {{ locales && locales.properties && locales.properties.vop_formula_fallback_none }}
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_used_surface }}:
          {{ vop.surfaceUsed }} m² ({{ locales && locales.properties && locales.properties.vop_formula_manual_surface }})
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_total }}: {{ vop.total }}
        </p>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='vop.total'
            valid-scope='modal-form'
            name='vop[total]'
            :label='locales && locales.properties && locales.properties.total_vop'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.extra_points }}
        <small class='calculate-price__pts-hint' v-if='ds.care_home && ds.care_home.points'>{{ formatPts(ds.care_home.points) }} pt</small>
      </h2>
      <div class='mb-10'>
        <div class='col-span-6'>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.care_home }}
          </h3>
          <input type='hidden' name='extra_points[care_home]' value='0'/>
          <theme-checkboxes
            id='careHome'
            @input='countExtraPoints'
            label=''
            :is-single-checkbox='true'
            name='extra_points[care_home]'
            v-model='extraPoints.care_home'
            :options="{ '1': `${locales && locales.properties && locales.properties.care_home} ${percentLabel(ds.care_home && ds.care_home.points, {suffix: locales && locales.properties && locales.properties.percentage_of_subtotal})}`}">
          </theme-checkboxes>
        </div>
      </div>
      <div class='mb-10'>
        <input type='hidden' name='extra_points[has_automatic_bell]' value='0'/>
        <theme-checkboxes
          id='careHomeBell'
          @input='countExtraPoints'
          label=''
          :is-single-checkbox='true'
          name='extra_points[has_automatic_bell]'
          v-model='extraPoints.has_automatic_bell'
          :options="{ '1': `${locales && locales.properties && locales.properties.has_automatic_bell} ${pointsLabel(ds.care_home && ds.care_home.automatic_bell_points)}`}">
        </theme-checkboxes>
      </div>
      <div class='mb-10'>
        <input type='hidden' name='extra_points[has_charging_station]' value='0'/>
        <theme-checkboxes
          id='chargingStation'
          @input='countExtraPoints'
          label=''
          :is-single-checkbox='true'
          name='extra_points[has_charging_station]'
          v-model='extraPoints.has_charging_station'
          :options="{ '1': `${locales && locales.properties && locales.properties.has_charging_station} ${pointsLabel(ds.care_home && ds.care_home.charging_station)}`}">
        </theme-checkboxes>
      </div>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.national_monument }}
        </h3>
        <theme-radios
          :columnDirection='true'
          v-model='extraPoints.national_monument.value'
          name='extra_points[type]'
          :options="{
            '0': `${locales && locales.properties && locales.properties.municipal_or_provincial_monument} ${percentLabel(ds.monument && ds.monument.municipal_or_provincial_monument_percentage, {suffix: locales && locales.properties && locales.properties.percentage_of_rent})}`,
            '1': `${locales && locales.properties && locales.properties.protected_town_and_village_view} ${percentLabel(ds.monument && ds.monument.protected_town_and_village_view_percentage, {suffix: locales && locales.properties && locales.properties.percentage_of_rent})}`,
            '2': `${locales && locales.properties && locales.properties.national_monument_before} ${pointsLabel(ds.monument && ds.monument.national_monument_points)}`,
            '3': `${locales && locales.properties && locales.properties.national_monument_after} ${percentLabel(ds.monument && ds.monument.national_monument_percentage, {suffix: locales && locales.properties && locales.properties.percentage_of_rent})}`,
          }"
          @input='countExtraPoints'>
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='extraPoints.total'
            name='extra_points[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.extra_points)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.deduction_points }}
      </h2>
      <p class='calculate-price__pts-hint mb-5' v-if='ds.deduction && ds.deduction.small_area_negative_points'>
        {{ locales && locales.properties && locales.properties.small_area_deduction_hint }} {{ pointsLabel(-Math.abs(parseFloat((ds.deduction.small_area_negative_points+'').replace(',', '.')))) }}
      </p>
      <div class='mb-10' v-for='(deduction, index) in deductionOptions'>
        <div class='mb-7' v-if="index != 'total'" :key='index'>
          <input type='hidden' :name='`deduction_points[` + index + `]`' value='0'/>
          <theme-checkboxes
            :id='`deductionOptions`+index'
            @input='countDeductionPoints'
            label=''
            :is-single-checkbox='true'
            :name='`deduction_points[` + index + `]`'
            v-model='deductionOptions[index].value'
            :options="{ '1': `${locales && locales.properties && locales.properties[index]} ${pointsLabel(-Math.abs(parseFloat((deductionOptions[index].points+'').replace(',', '.')) || 0))}` }">
          </theme-checkboxes>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='deductionOptions.total'
            name='deduction_points[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.deduction_points_total)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-bottom'>
      <div class='default-content grid grid-cols-1 lg:grid-cols-12 items-center gap-1 lg:gap-5'>
        <div class='col-span-2 hidden lg:block'>
          <h3 class='calculate-price__form-bottom-title'>
            {{ this.locales && this.locales.common && this.locales.common.total }}
          </h3>
        </div>
        <div class='lg:col-span-7'>
          <div class='grid grid-cols-2 items-center gap-5'>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>{{ locales && locales.properties && locales.properties.total_points }}</div>
              <div class='lg:hidden'>{{totalPoints || '0'}}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='totalPoints'
                  name='total'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>{{locales && locales.properties && locales.properties.max_basic_rent_price }}</div>
              <div class='lg:hidden'>{{price || '0'}}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='price'
                  name='price'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
          </div>
        </div>
        <div class='lg:col-span-3 grid grid-cols-2 items-center gap-5'>
          <button type='submit' class='theme-btn theme-btn--theme'>
            {{ locales && locales.common && locales.common.download_pdf }}
          </button>
          <button type='submit' name='submit' value='save' class='theme-btn theme-btn--theme'>
            {{ locales && locales.common && locales.common.save }}
          </button>
        </div>
        <div v-if='refreshUrl' class='hidden lg:block lg:col-span-3 mt-3'>
          <a href='#' class='theme-btn theme-btn--secondary' @click.prevent='confirmRefresh'>
            ↺ {{ locales && locales.properties && locales.properties.refresh_from_property }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import _ from 'lodash';
  import $ from 'jquery';

  export default {
    computed: {
      ...mapGetters(['metadata', 'locales']),

      // Same convention already used elsewhere (app.js, VideoPlayer.vue): the current
      // display locale is exposed once on <body data-lang="..."> by the layout, driven by
      // Config::get('app.locale') server-side — never re-derived or passed as a prop.
      currentLocale() {
        var body = document.querySelector('body');
        return (body && body.getAttribute('data-lang')) || 'nl';
      },

      ds() {
        return (this.dependantSettings && this.dependantSettings.price_points_settings) || {};
      },

      maxHeatingPts() {
        // Fallback must match countHeatingPoints()'s own default (7.5) - this
        // hint previously used a different hardcoded fallback (8), which
        // could display a value inconsistent with the actual calculation
        // whenever dependant_heating.max_points was missing from settings.
        var v = this.ds.dependant_heating && this.ds.dependant_heating.max_points;
        var n = parseFloat(String(v || '7.5').replace(',', '.'));
        return (isNaN(n) || n <= 0) ? 7.5 : n;
      },

      /**
       * energyLabelsCustomOrder (server-built [id, label] pairs) drives the
       * radios' actual rendering when sort-by="custom" - ThemeRadios reads
       * customOrder directly and ignores the `options` prop in that mode.
       */
      energyLabelsCustomOrderWithPoints() {
        var pps = this.ds.energy || {};
        var per_m2 = this.locales && this.locales.properties && this.locales.properties.per_m2;
        var self = this;
        return (this.energyLabelsCustomOrder || []).map(function (pair) {
          var id = pair[0];
          var label = pair[1];
          var rate = pps[id] && pps[id].points_per_m2;
          if (rate === undefined || rate === null || rate === '') {
            return [id, label];
          }
          return [id, label + ' ' + self.pointsLabel(rate, {suffix: per_m2})];
        });
      },

      maxKitchenPts() {
        var wl = this.kitchen && this.kitchen.worktop_length && this.kitchen.worktop_length.value;
        var k = this.ds.kitchen || {};
        var keyMap = { '2': 'worktop_1_2m_max_points', '3': 'worktop_2_3m_max_points', '4': 'worktop_3m_max_points', '5': 'worktop_5m_max_points' };
        var key = keyMap[String(wl)];
        if (!key || !k[key]) return null;
        var n = parseFloat(String(k[key]).replace(',', '.'));
        return isNaN(n) ? null : n;
      },

      maxSanitaryPts() {
        var sb = this.sanitary && this.sanitary.shower_bath && this.sanitary.shower_bath.value;
        var s = this.ds.sanitary || {};
        var keyMap = { '1': 'shower_max_points', '2': 'bath_max_points', '3': 'shower_bath_max_points' };
        var key = keyMap[String(sb)];
        if (!key || !s[key]) return null;
        var n = parseFloat(String(s[key]).replace(',', '.'));
        return isNaN(n) ? null : n;
      },

      maxParkingPts() {
        var sp = this.ds.shared_parking;
        if (!sp) return null;
        var max = 0;
        Object.keys(sp).forEach(function(k) {
          if (k !== 'ev_charging_points') {
            var v = parseFloat(String(sp[k]).replace(',', '.'));
            if (!isNaN(v) && v > max) max = v;
          }
        });
        return max > 0 ? max : null;
      },
      sharedParkingOptionsWithPoints() {
        var sp = this.ds.shared_parking || {};
        var out = {};
        var self = this;
        Object.keys(this.sharedParkingOptions || {}).forEach(function (id) {
          var label = self.sharedParkingOptions[id];
          var rate = sp[id];
          out[id] = (rate !== undefined && rate !== null && rate !== '')
            ? label + ' ' + self.pointsLabel(rate)
            : label;
        });
        return out;
      },
    },
    data() {
      return {
        total: 0,
        totalPoints: 0,
        surface: 0,
        price: 0,
        _mountComplete: false,
      };
    },
    props: {
      property: {
        default: () => {
          return {};
        },
        type: Object,
      },
      facilityOptionsByCategory: {
        default: () => {
          return {};
        },
        type: Object,
      },
      kitchenFacilities: {
        default: () => {
          return {};
        },
        type: Object,
      },
      extraPoints: {
        default: () => {
          return {};
        },
        type: Object,
      },
      leaveArea: {
        default: () => {
          return {};
        },
        type: Object,
      },
      heating: {
        default: () => {
          return {};
        },
        type: Object,
      },
      dependantMaxRentalPrices: {
        default: () => {
          return {};
        },
        type: [Array, Object],
      },
      kitchenOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      wcOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      bathShowerOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      washbasinOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      outdoorSpaceOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      bicycleShedOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      deductionOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      dependantSettings: {
        default: () => {
          return {};
        },
        type: [Array, Object],
      },
      otherSpaces: {
        default: () => {
          return {};
        },
        type: Object,
      },
      kitchen: {
        default: () => {
          return {};
        },
        type: Object,
      },
      sanitary: {
        default: () => {
          return {};
        },
        type: Object,
      },
      outdoor: {
        default: () => {
          return {};
        },
        type: Object,
      },
      indoor: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energy: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energyLabels: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energyLabelsOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energyLabelsCustomOrder: {
        default: () => {
          return {};
        },
        type: [Object, Array],
      },
      outdoorSpacesCustomOrder: {
        default: () => {
          return {};
        },
        type: [Object, Array],
      },
      energyLabels: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energyLabelsBySlug: {
        default: () => {
          return {};
        },
        type: Object,
      },
      energyLabelsIdBySlug: {
        default: () => {
          return {};
        },
        type: Object,
      },
      disabled: {
        default: () => {
          return {};
        },
        type: Object,
      },
      dependantLandlordSpendingsBase: {
        default: 0,
        type: [String, Number],
      },
      sharedParking: {
        default: () => {
          return {};
        },
        type: Object,
      },
      sharedParkingOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      vop: {
        default: () => {
          return {};
        },
        type: Object,
      },
      refreshUrl: {
        default: '',
        type: String,
      },
    },
    beforeMount() {
      this.countDependantLeaveAreaPoints();
      this.countDependantOtherSpacesAreaPoints();
      this.countHeatingPoints();
      this.countEnergyPoints();
      this.countKitchenPoints();
      this.countSanitaryPoints();
      this.countDisabledPoints();
      this.countOutdoorPoints();
      this.countIndoorPoints();
      this.countSharedParkingPoints();
      this.countVopPoints();
      this.countExtraPoints();
      this.countDeductionPoints();
    },
    mounted() {
      // VueNumeric child components re-emit their DOM value on mount, which
      // would call calculateLeaveAreaSurface and overwrite saved surfaces with
      // facility width×length dimensions.  _mountComplete gates that method so
      // it only runs after the user actually changes a field.
      // $nextTick defers until after all child input events have flushed so we
      // can then recalculate from the correct (saved) surfaces.
      this.$nextTick(() => {
        this._mountComplete = true;
        this.countDependantLeaveAreaPoints();
        this.countDependantOtherSpacesAreaPoints();
        this.countExtraPoints();
      });
    },
    methods: {
      // Locale-aware display name for a room/space/facility entry — never used for what gets
      // submitted (name_nl/name_en hidden inputs keep sending both, unconditionally, so the
      // bilingual data contract on the backend is untouched by which language is displayed).
      // Fallback order: requested-language name -> Dutch name -> any other available localized
      // name -> a slug/type fallback so a legacy row missing both never renders blank.
      localizedName(entity, fallback) {
        if (!entity) return fallback || '';
        var locale = this.currentLocale;
        if (entity.value_lang) {
          var localized = entity.value_lang[locale] || entity.value_lang.nl || entity.value_lang.en;
          if (localized) return localized;
        }
        var requested = entity['name_' + locale];
        if (requested) return requested;
        if (entity.name_nl) return entity.name_nl;
        if (entity.name_en) return entity.name_en;
        if (entity.value) return entity.value;
        return fallback || entity.slug || entity.wws_key || '';
      },
      confirmRefresh() {
        const msg = this.locales && this.locales.properties && this.locales.properties.refresh_from_property_confirm
          || 'This will overwrite unsaved form changes with the current property data. Continue?';
        if (window.confirm(msg)) {
          window.location.href = this.refreshUrl;
        }
      },
      /**
       * Round to nearest 0.25 pt per WWS policy 2.1.6: midpoint 0.125 rounds up.
       */
      roundToQuarter(val) {
        return Math.round(val * 4) / 4;
      },

      countDependantLeaveAreaPoints() {
        var sum = 0;
        var tenantSurface = 0;
        var ownRoomPts = parseFloat((this.dependantSettings.price_points_settings.dependant_leave_area.own_room_points_per_m2 + "").replace(",", "."));
        var commonPts = parseFloat((this.dependantSettings.price_points_settings.dependant_leave_area.common_area_points_per_m2 + "").replace(",", "."));

        var spaces = this.leaveArea.spaces || [];
        for (var i = 0; i < spaces.length; i++) {
          var space = spaces[i];
          var surf = parseFloat((space.surface + "").replace(",", ".")) || 0;
          if (space.is_private == 1) {
            sum += surf * ownRoomPts;
            tenantSurface += surf;
          } else if (space.is_common == 1) {
            var residents = Math.max(1, parseInt(space.shared_addresses) || 1);
            sum += (surf / residents) * commonPts;
            tenantSurface += surf / residents;
          }
        }

        this.surface = tenantSurface;
        Vue.set(this.leaveArea, "total", this.roundToQuarter(sum));
        // Energy (R4) and deductions (R13) both use this.surface, so they must
        // recalculate whenever the leave-area surface changes.
        this.countEnergyPoints();
        this.countDeductionPoints();
        this.countDependantTotal();
      },

      countDependantOtherSpacesAreaPoints() {
        var sum = 0;
        var privatePts = parseFloat((this.dependantSettings.price_points_settings.dependant_other_spaces_area.private_area_points_per_m2 + "").replace(",", "."));
        var commonPts = parseFloat((this.dependantSettings.price_points_settings.dependant_other_spaces_area.common_area_points_per_m2 + "").replace(",", "."));

        var spaces = this.otherSpaces.spaces || [];
        for (var i = 0; i < spaces.length; i++) {
          var space = spaces[i];
          var surf = parseFloat((space.surface + "").replace(",", ".")) || 0;
          if (space.is_private == 1) {
            sum += surf * privatePts;
          } else if (space.is_common == 1) {
            var residents = Math.max(1, parseInt(space.shared_addresses) || 1);
            sum += (surf / residents) * commonPts;
          }
        }

        Vue.set(this.otherSpaces, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },

      countHeatingPoints() {
        var sum = 0;
        var ownRoomHeat    = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.own_room + "").replace(",", "."));
        var commonRoomHeat = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.common_room + "").replace(",", "."));
        var ownRoomCool    = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.own_room_cooling + "").replace(",", "."));
        var commonRoomCool = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.common_room_cooling + "").replace(",", "."));
        var ownCoolMax     = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.own_room_max_cooling + "").replace(",", "."));
        var commonCoolMax  = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.common_room_max_cooling + "").replace(",", "."));
        var otherOwnHeat   = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.other_own_room + "").replace(",", "."));
        var otherCommonHeat = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.other_common_room + "").replace(",", "."));

        // R1: leaveArea rooms
        var privateHeat = 0, sharedHeat = 0, privateCool = 0, sharedCool = 0;
        var leaveSpaces = this.leaveArea.spaces || [];
        for (var i = 0; i < leaveSpaces.length; i++) {
          var s = leaveSpaces[i];
          var res = Math.max(1, parseInt(s.shared_addresses) || 1);
          if (s.is_private == 1) {
            if (s.heated == 1) privateHeat += ownRoomHeat;
            if (s.cooled == 1) privateCool += ownRoomCool;
          } else if (s.is_common == 1) {
            if (s.heated == 1) sharedHeat += Math.round((commonRoomHeat / res + Number.EPSILON) * 100) / 100;
            if (s.cooled == 1) sharedCool += Math.round((commonRoomCool / res + Number.EPSILON) * 100) / 100;
          }
        }
        if (privateCool > ownCoolMax) privateCool = ownCoolMax;
        if (sharedCool > commonCoolMax) sharedCool = commonCoolMax;
        sum += privateHeat + sharedHeat + privateCool + sharedCool;

        // R2: otherSpaces rooms
        var otherPrivateHeat = 0, otherSharedHeat = 0;
        var otherSpacesList = this.otherSpaces.spaces || [];
        for (var j = 0; j < otherSpacesList.length; j++) {
          var os = otherSpacesList[j];
          var ores = Math.max(1, parseInt(os.shared_addresses) || 1);
          if (os.is_private == 1) {
            if (os.heated == 1) otherPrivateHeat += otherOwnHeat;
          } else if (os.is_common == 1) {
            if (os.heated == 1) otherSharedHeat += Math.round((otherCommonHeat / ores + Number.EPSILON) * 100) / 100;
          }
        }
        sum += otherPrivateHeat + otherSharedHeat;

        var maxHeatingPoints = parseFloat(((this.dependantSettings.price_points_settings.dependant_heating.max_points || "7.5") + "").replace(",", "."));
        if (isNaN(maxHeatingPoints) || maxHeatingPoints <= 0) maxHeatingPoints = 7.5;
        sum = Math.min(sum, maxHeatingPoints);

        Vue.set(this.heating, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      countEnergyPoints() {
        var sum = 0;
        var energyPoints = 0;
        if(this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] != "no_label_or_index"){
          energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[this.energy.energy_label].points_per_m2+'').replace(',', '.'));
        }else if (this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] == "no_label_or_index" && this.energy.building_construction_year) {
          if (this.energy.building_construction_year <= 1976) {
            var labelG = this.energyLabelsIdBySlug["label_g"];
            energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelG].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year <= 1978) {
            var labelF = this.energyLabelsIdBySlug["label_f"];
              energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelF].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year <= 1983) {
            var labelE = this.energyLabelsIdBySlug["label_e"];
              energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelE].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year <= 1991) {
            var labelD = this.energyLabelsIdBySlug["label_d"];
              energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelD].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year <= 1999) {
            var labelC = this.energyLabelsIdBySlug["label_c"];
              energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelC].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year <= 2001) {
            var labelB = this.energyLabelsIdBySlug["label_b"];
            energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelB].points_per_m2+'').replace(',', '.'));
          } else if (this.energy.building_construction_year > 2001) {
            var labelA = this.energyLabelsIdBySlug["label_a"];
            energyPoints = parseFloat((this.dependantSettings.price_points_settings.energy[labelA].points_per_m2+'').replace(',', '.'));
          }
        }
        if (this.energy.monument_allowance && this.energy.monument_allowance == 1 && energyPoints < 0) {
          energyPoints = 0;
        }
        energyPoints = energyPoints * this.surface;
        sum += energyPoints;
        var total = this.roundToQuarter(sum);
        Vue.set(this.energy, "energy_points", total);
        Vue.set(this.energy, "total", total);
        this.countDependantTotal();
      },
      countKitchenPoints() {
        var sum = 0;
        var maxPoints = 0;
        var addPoints = 0;
        var residents = parseFloat((this.kitchen.number_of_residents + "").replace(",", "."));
        if (this.kitchen.worktop_length.value == 2) {
          addPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_1_2m_points + "").replace(",", ".")); //4
          maxPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_1_2m_max_points + "").replace(",", ".")); //8

        } else if (this.kitchen.worktop_length.value == 3) {
          addPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_2_3m_points + "").replace(",", ".")); //7
          maxPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_2_3m_max_points + "").replace(",", ".")); //14
        } else if (this.kitchen.worktop_length.value == 4) {
          addPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_3m_points + "").replace(",", ".")); //7
          maxPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_3m_max_points + "").replace(",", ".")); //14
        } else if (this.kitchen.worktop_length.value == 5) {
          addPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_5m_points + "").replace(",", ".")); //7
          maxPoints = parseFloat((this.dependantSettings.price_points_settings.kitchen.worktop_5m_max_points + "").replace(",", ".")); //14
        }
        Vue.set(this.kitchen.worktop_length, "points", addPoints);
        Vue.set(this.kitchen.worktop_length, "total", addPoints);
        sum += addPoints;

        // For shared kitchens (residents > 1): accumulate without the worktop max cap,
        // matching the backend which applies kitchen_for_max_5_rooms only after dividing.
        var effectiveMax = (residents > 1) ? Infinity : maxPoints;

        for (var ob in this.kitchen) {
          var element = this.kitchen[ob];
          var points = 0;
          if (ob == "cooker") {
            if (element.value == 1) {
              points = parseFloat((this.dependantSettings.price_points_settings.kitchen.gas_cooker_points + "").replace(",", "."));
            } else if (element.value == 2) {
              points = parseFloat((this.dependantSettings.price_points_settings.kitchen.ceramic_cooker_points + "").replace(",", "."));
            } else if (element.value == 3) {
              points = parseFloat((this.dependantSettings.price_points_settings.kitchen.induction_cooker_points + "").replace(",", "."));
            }
            Vue.set(this.kitchen[ob], "total", points);
          } else if (ob == "oven") {
            if (element.value == 1) {
              points = parseFloat((this.dependantSettings.price_points_settings.kitchen.gas_oven_points + "").replace(",", "."));
            } else if (element.value == 2) {
              points = parseFloat((this.dependantSettings.price_points_settings.kitchen.electric_oven_points + "").replace(",", "."));
            }
            Vue.set(this.kitchen[ob], "total", points);
          } else if (ob != "total" && ob != "worktop_length" && ob != "number_of_residents") {
            points = parseFloat((element.points + "").replace(",", ".")) * Math.round(parseFloat((element.value + "").replace(",", ".")));
            Vue.set(this.kitchen[ob], "total", points);
          }
          if (sum + points <= effectiveMax) {
            sum += points;
          }
        }
        if (residents > 0) {
          sum = sum / residents;
          // Apply shared kitchen cap after dividing (policy: kitchen_for_max_5_rooms)
          if (residents > 1) {
            var sharedCap = parseFloat((this.dependantSettings.price_points_settings.kitchen.kitchen_for_max_5_rooms + "").replace(",", "."));
            if (!isNaN(sharedCap) && sharedCap > 0) {
              sum = Math.min(sum, sharedCap);
            }
          }
        }

        Vue.set(this.kitchen, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      // Sanitary: the "douche/bad" cap bounds ONLY the base shower/bath points plus the
      // douche/bad "extra" features, combined — not toilets/hunged toilets/washbasins/the
      // separate-toilet correction (tracked in toiletPoints, which shares the
      // residents-division treatment below). The cap is applied to the AGGREGATE extras
      // total (min(base + extras, cap)), not sequentially per field, so the result no
      // longer depends on object-key iteration order. Mirrors
      // DependentRuleset2026::calcDependantSanitary on the backend.
      countSanitaryPoints() {
        var sum = 0;
        var basePoints = 0;
        var doucheBadCap = 0;
        var toiletPoints = 0;

        if (this.sanitary.shower_bath.value == 1) {
          basePoints = parseFloat((this.dependantSettings.price_points_settings.sanitary.shower_points +'').replace(',', '.')); //3
          doucheBadCap = parseFloat((this.dependantSettings.price_points_settings.sanitary.shower_max_points +'').replace(',', '.')); //6
        } else if (this.sanitary.shower_bath.value == 2) {
          basePoints = parseFloat((this.dependantSettings.price_points_settings.sanitary.bath_points +'').replace(',', '.')); //5
          doucheBadCap = parseFloat((this.dependantSettings.price_points_settings.sanitary.bath_max_points +'').replace(',', '.')); //10
        } else if (this.sanitary.shower_bath.value == 3) {
          basePoints = parseFloat((this.dependantSettings.price_points_settings.sanitary.shower_bath_points +'').replace(',', '.')); //6
          doucheBadCap = parseFloat((this.dependantSettings.price_points_settings.sanitary.shower_bath_max_points +'').replace(',', '.')); //12
        }
        Vue.set(this.sanitary.shower_bath, "points", basePoints);
        Vue.set(this.sanitary.shower_bath, "total", basePoints);

        if (this.sanitary.number_of_toilets.value) {
          var p = parseFloat((this.sanitary.number_of_toilets.points+'').replace(',', '.')) * Math.round(parseFloat(((this.sanitary.number_of_toilets.value || 0) + "").replace(",", ".")));
          toiletPoints += p;
          Vue.set(this.sanitary.number_of_toilets, "total", p);
        }
        if (this.sanitary.number_of_hunged_toilets.value) {
          var p = parseFloat((this.sanitary.number_of_hunged_toilets.points+'').replace(',', '.')) * Math.round(parseFloat(((this.sanitary.number_of_hunged_toilets.value || 0) + "").replace(",", ".")));
          toiletPoints += p;
          Vue.set(this.sanitary.number_of_hunged_toilets, "total", p);
        }
        if (this.sanitary.number_of_washbasins.value) {
          var p = parseFloat((this.sanitary.number_of_washbasins.points+'').replace(',', '.')) * Math.round(parseFloat(((this.sanitary.number_of_washbasins.value || 0) + "").replace(",", ".")));
          toiletPoints += p;
          Vue.set(this.sanitary.number_of_washbasins, "total", p);
        }

        var extrasSum = 0;
        // Official rule: power outlets are valued per washbasin (max 2 per washbasin),
        // so the cap must scale with the number of washbasins, not be a flat constant.
        var washbasinCount = Math.round(parseFloat(((this.sanitary.number_of_washbasins.value || 0) + "").replace(",", ".")) || 0);
        var powerOutletMax = washbasinCount * parseFloat((this.dependantSettings.price_points_settings.sanitary.power_outlet_max_points + "").replace(",", "."));

        for (var ob in this.sanitary) {
          var element = this.sanitary[ob];
          var points = 0;
          if (ob == "power_outlet") {
            points = (this.sanitary.number_of_power_outlet.value || 0) * parseFloat((element.points+'').replace(',', '.'));
            if (points > powerOutletMax) {
              points = powerOutletMax;
            }
            Vue.set(this.sanitary[ob], "total", points);
            extrasSum += points;
          } else if (ob == "number_of_separate_toilets") {
            if(parseFloat(((this.sanitary.number_of_toilets.value || 0) +'').replace(',', '.')) < parseFloat(((this.sanitary.number_of_separate_toilets.value || 0)+'').replace(',', '.'))){
              var nt =  this.sanitary.number_of_toilets.value;
              Vue.set(this.sanitary.number_of_separate_toilets, "value", nt);
            }
            points = -(parseFloat(((this.sanitary.number_of_toilets.value || 0) + '').replace(',', '.')) - parseFloat(((this.sanitary.number_of_separate_toilets.value || 0)+'').replace(',', '.'))) * parseFloat((element.points+'').replace(',', '.'));
            Vue.set(this.sanitary[ob], "total", points);
            toiletPoints += points;
          } else if (
            ob != "total" &&
            ob != "number_of_toilets" &&
            ob != "number_of_hunged_toilets" &&
            ob != "number_of_residents" &&
            ob != "number_of_separate_toilets" &&
            ob != "number_of_own_toilets" &&
            ob != "number_of_own_separate_toilets" &&
            ob != "number_of_own_hunged_toilets" &&
            ob != "number_of_washbasins" &&
            ob != "number_of_power_outlet" &&
            ob != "shower_bath" &&
            ob != "own_bathroom"
          ) {
              points = parseFloat((element.points+'').replace(',', '.')) * Math.round(parseFloat((element.value + "").replace(",", ".")) || 0);
              Vue.set(this.sanitary[ob], "total", points);
              extrasSum += points;
          }
        }

        var sharedBathSum = Math.min(basePoints + extrasSum, doucheBadCap);

        var residents = this.sanitary.number_of_residents ? Math.round(parseFloat((this.sanitary.number_of_residents + "").replace(",", "."))) : 0;
        if (residents > 0) {
          toiletPoints = toiletPoints / residents;
          // For communal (own_bathroom=0): full bathroom is shared → divide shower+extras too.
          // For private (own_bathroom=1): only toilet/washbasin/hung are divided.
          if (this.sanitary.own_bathroom == 0 || this.sanitary.own_bathroom === null) {
            sum = (sharedBathSum / residents) + toiletPoints;
          } else {
            sum = sharedBathSum + toiletPoints;
          }
        } else {
          toiletPoints = 0;
          if (this.sanitary.own_bathroom == 0 || this.sanitary.own_bathroom === null) {
            sum = 0;
          } else {
            sum = sharedBathSum;
          }
        }
        if (this.sanitary.number_of_own_toilets.value) {
          var p = parseFloat((this.sanitary.number_of_own_toilets.points+'').replace(',', '.')) * Math.round(parseFloat((this.sanitary.number_of_own_toilets.value + "").replace(",", ".")));
          sum += p;
          Vue.set(this.sanitary.number_of_own_toilets, "total", p);
        }
        if ((this.sanitary.number_of_own_separate_toilets.value || 0)) {
          if(parseFloat(((this.sanitary.number_of_own_toilets.value || 0)+'').replace(',', '.')) < parseFloat(((this.sanitary.number_of_own_separate_toilets.value || 0)+'').replace(',', '.'))){
            var nt =  this.sanitary.number_of_own_toilets.value;
            Vue.set(this.sanitary.number_of_own_separate_toilets, "value", nt);
          }
          points = -(parseFloat(((this.sanitary.number_of_own_toilets.value || 0)+'').replace(',', '.')) - parseFloat(((this.sanitary.number_of_own_separate_toilets.value || 0)+'').replace(',', '.'))) * parseFloat((this.sanitary['number_of_own_separate_toilets'].points+'').replace(',', '.'));
          sum += points;
          Vue.set(this.sanitary['number_of_own_separate_toilets'], "total", points);
        }
        if ((this.sanitary.number_of_own_hunged_toilets.value || 0)) {
          if(parseFloat(((this.sanitary.number_of_own_toilets.value || 0)+'').replace(',', '.')) < parseFloat(((this.sanitary.number_of_own_hunged_toilets.value || 0)+'').replace(',', '.'))){
            var nt =  this.sanitary.number_of_own_toilets.value;
            Vue.set(this.sanitary.number_of_own_hunged_toilets, "value", nt);
          }
          points = parseFloat(((this.sanitary.number_of_own_hunged_toilets.value || 0)+'').replace(',', '.')) * parseFloat((this.sanitary['number_of_own_hunged_toilets'].points+'').replace(',', '.'));
          sum += points;
          Vue.set(this.sanitary['number_of_own_hunged_toilets'], "total", points);
        }
        Vue.set(this.sanitary, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      countDisabledPoints() {
        var sum = 0;
        var maxPoints = 0;
        if (this.disabled.total_investment.value && parseFloat(((this.disabled.total_investment.value || 0) + "").replace(",", ".")) > 0) {
          var p = parseFloat(((this.disabled.total_investment.points || 0) + "").replace(",", ".")) * Math.floor(parseFloat((this.disabled.total_investment.value + "").replace(",", ".") / this.dependantLandlordSpendingsBase));
          sum += p;
          Vue.set(this.disabled.total_investment, "total", p);
        }

        var residents = this.disabled.number_of_residents ? Math.round(parseFloat((this.disabled.number_of_residents + "").replace(",", "."))) : 0;
        if(residents > 0){
          sum = sum / residents;
        }else{
          sum =0;
        }
        Vue.set(this.disabled, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      setLeaveAreaSpaceType(idx, val) {
        this.leaveArea.spaces[idx].is_private = val === 'private' ? 1 : 0;
        this.leaveArea.spaces[idx].is_common  = val === 'common'  ? 1 : 0;
        this.countDependantLeaveAreaPoints();
        this.countHeatingPoints();
      },
      setOtherSpaceType(idx, val) {
        this.otherSpaces.spaces[idx].is_private = val === 'private' ? 1 : 0;
        this.otherSpaces.spaces[idx].is_common  = val === 'common'  ? 1 : 0;
        this.countDependantOtherSpacesAreaPoints();
        this.countHeatingPoints();
      },
      sumExtraDimensions(rows) {
        if (!rows || !rows.length) return 0;
        var sum = 0;
        for (var i = 0; i < rows.length; i++) {
          var w = parseFloat((rows[i].width + '').replace(',', '.'));
          var l = parseFloat((rows[i].length + '').replace(',', '.'));
          if (isNaN(w)) w = 0;
          if (isNaN(l)) l = 0;
          sum += w * l;
        }
        return sum;
      },
      addLeaveAreaExtraDimension(idx) {
        var sp = this.leaveArea.spaces[idx];
        if (!sp.extra_dimensions) Vue.set(sp, 'extra_dimensions', []);
        sp.extra_dimensions.push({ length: '', width: '' });
      },
      removeLeaveAreaExtraDimension(idx, dIdx) {
        var sp = this.leaveArea.spaces[idx];
        sp.extra_dimensions.splice(dIdx, 1);
        // Explicit user action, not a mount-time re-emit — recompute unconditionally.
        var w = parseFloat((sp.width + '').replace(',', '.')) || 0;
        var l = parseFloat((sp.length + '').replace(',', '.')) || 0;
        Vue.set(sp, 'surface', w * l + this.sumExtraDimensions(sp.extra_dimensions));
        this.countDependantLeaveAreaPoints();
      },
      addOtherSpaceExtraDimension(idx) {
        var sp = this.otherSpaces.spaces[idx];
        if (!sp.extra_dimensions) Vue.set(sp, 'extra_dimensions', []);
        sp.extra_dimensions.push({ length: '', width: '' });
      },
      removeOtherSpaceExtraDimension(idx, dIdx) {
        var sp = this.otherSpaces.spaces[idx];
        sp.extra_dimensions.splice(dIdx, 1);
        // Explicit user action, not a mount-time re-emit — recompute unconditionally.
        var w = parseFloat((sp.width + '').replace(',', '.')) || 0;
        var l = parseFloat((sp.length + '').replace(',', '.')) || 0;
        Vue.set(sp, 'surface', w * l + this.sumExtraDimensions(sp.extra_dimensions));
        this.countDependantOtherSpacesAreaPoints();
      },
      calculateLeaveAreaSurface(idx) {
        if (!this._mountComplete) return;
        var sp = this.leaveArea.spaces[idx];
        var w = parseFloat((sp.width + '').replace(',', '.')) || 0;
        var l = parseFloat((sp.length + '').replace(',', '.')) || 0;
        var extra = this.sumExtraDimensions(sp.extra_dimensions);
        if ((w > 0 && l > 0) || extra > 0) Vue.set(sp, 'surface', w * l + extra);
        this.countDependantLeaveAreaPoints();
      },
      calculateOtherSpaceSurface(idx) {
        if (!this._mountComplete) return;
        var sp = this.otherSpaces.spaces[idx];
        var w = parseFloat((sp.width + '').replace(',', '.')) || 0;
        var l = parseFloat((sp.length + '').replace(',', '.')) || 0;
        var extra = this.sumExtraDimensions(sp.extra_dimensions);
        if ((w > 0 && l > 0) || extra > 0) Vue.set(sp, 'surface', w * l + extra);
        this.countDependantOtherSpacesAreaPoints();
      },
      addLeaveAreaSpace() {
        var spaces = this.leaveArea.spaces || [];
        spaces.push({ wws_key: '', name_nl: '', name_en: '', slug: '', is_extra: true, facility_metadata_id: '', is_private: 1, is_common: 0, shared_addresses: 1, width: '', length: '', surface: '', extra_dimensions: [], heated: 0, cooled: 0 });
        Vue.set(this.leaveArea, 'spaces', spaces);
      },
      addOtherSpace() {
        var spaces = this.otherSpaces.spaces || [];
        spaces.push({ wws_key: '', name_nl: '', name_en: '', slug: '', is_extra: true, facility_metadata_id: '', is_private: 1, is_common: 0, shared_addresses: 1, width: '', length: '', surface: '', extra_dimensions: [], heated: 0, cooled: 0 });
        Vue.set(this.otherSpaces, 'spaces', spaces);
      },
      calculateOutdoorSpaceArea(idx) {
        if (!this._mountComplete) return;
        var sp = this.outdoor.spaces[idx];
        var w = parseFloat((sp.width + '').replace(',', '.')) || 0;
        var l = parseFloat((sp.length + '').replace(',', '.')) || 0;
        if (w > 0 && l > 0) Vue.set(sp, 'area', w * l);
        this.countOutdoorPoints();
      },
      setOutdoorSpaceType(idx, val) {
        this.outdoor.spaces[idx].is_private = val === 'private' ? 1 : 0;
        this.outdoor.spaces[idx].is_common  = val === 'common'  ? 1 : 0;
        this.countOutdoorPoints();
      },
      addOutdoorSpace() {
        var spaces = this.outdoor.spaces || [];
        spaces.push({ name_nl: '', name_en: '', is_extra: true, facility_metadata_id: '', is_private: 1, is_common: 0, residents: 1, shared_addresses: 1, width: '', length: '', area: '' });
        Vue.set(this.outdoor, 'spaces', spaces);
      },
      onLeaveAreaSpaceSelect(idx) {
        var facId = this.leaveArea.spaces[idx].facility_metadata_id;
        if (!facId || !this.facilityOptionsByCategory) return;
        var fac = (this.facilityOptionsByCategory.room || []).find(function(f) { return f.id == facId; });
        if (fac) { this.leaveArea.spaces[idx].name_nl = fac.name_nl; this.leaveArea.spaces[idx].name_en = fac.name_en; }
      },
      onOtherSpaceSelect(idx) {
        var facId = this.otherSpaces.spaces[idx].facility_metadata_id;
        if (!facId || !this.facilityOptionsByCategory) return;
        var fac = (this.facilityOptionsByCategory.other_room || []).find(function(f) { return f.id == facId; });
        if (fac) { this.otherSpaces.spaces[idx].name_nl = fac.name_nl; this.otherSpaces.spaces[idx].name_en = fac.name_en; }
      },
      onOutdoorSpaceSelect(idx) {
        var facId = this.outdoor.spaces[idx].facility_metadata_id;
        if (!facId || !this.facilityOptionsByCategory) return;
        var fac = (this.facilityOptionsByCategory.outdoor_space || []).find(function(f) { return f.id == facId; });
        if (fac) { this.outdoor.spaces[idx].name_nl = fac.name_nl; this.outdoor.spaces[idx].name_en = fac.name_en; }
      },
      countOutdoorPoints() {
        var sum = 0;
        var minPts    = parseFloat((this.dependantSettings.price_points_settings.outdoor.min_space_points + '').replace(",", "."));
        var privatePts = parseFloat((this.dependantSettings.price_points_settings.outdoor.space_points + '').replace(",", "."));
        var commonPts  = parseFloat((this.dependantSettings.price_points_settings.outdoor.common_space_points + '').replace(",", "."));
        var maxPts     = parseFloat((this.dependantSettings.price_points_settings.outdoor.max_space_points + '').replace(",", "."));

        // Official rule (2.8 Buitenruimten): the presence bonus (minPts) is awarded ONCE
        // for having any private outdoor space at all, not once per entry — private areas
        // are summed first and the bonus applied once, after the loop, on the total.
        var spaces = this.outdoor.spaces || [];
        var privateAreaSum = 0;
        for (var i = 0; i < spaces.length; i++) {
          var space = spaces[i];
          var area = parseFloat((space.area + '').replace(",", ".")) || 0;
          if (area <= 0) continue;
          if (space.is_private == 1) {
            privateAreaSum += area;
          } else if (space.is_common == 1) {
            var addrs = Math.max(1, parseInt(space.shared_addresses) || 1);
            var res   = Math.max(1, parseInt(space.residents) || 1);
            sum += (area * commonPts) / (addrs * res);
          }
        }
        if (privateAreaSum > 0) {
          sum += minPts + privateAreaSum * privatePts;
        }

        if (sum > maxPts) sum = maxPts;
        Vue.set(this.outdoor, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      countIndoorPoints() {
        var sum = 0;
        if (this.indoor.common_departure && this.indoor.common_departure.value && parseFloat((this.indoor.common_departure.value + "").replace(",", ".")) > 0 && parseFloat((this.indoor.common_departure.number_of_addresses + "").replace(",", ".")) > 0) {
          var addresses = parseFloat((this.indoor.common_departure.number_of_addresses + "").replace(",", "."));
          var units = Math.max(1, parseFloat((this.indoor.common_departure.units_per_address || 1) + ""));
          var p = (parseFloat((this.indoor.common_departure.value + "").replace(",", ".")) * parseFloat((this.dependantSettings.price_points_settings.common_indoor_shared_with_multiple_addresses.common_departure_points+'').replace(",", "."))) / (addresses * units);
          Vue.set(this.indoor.common_departure, "total", p);
          sum += p;
        }
        if (this.indoor.common_other_space && this.indoor.common_other_space.value && parseFloat((this.indoor.common_other_space.value + "").replace(",", ".")) > 0 && parseFloat((this.indoor.common_other_space.common_other_space_addresses + "").replace(",", ".")) > 0) {
          var addresses = parseFloat((this.indoor.common_other_space.common_other_space_addresses + "").replace(",", "."));
          var units = Math.max(1, parseFloat((this.indoor.common_other_space.units_per_address || 1) + ""));
          var p = (parseFloat((this.indoor.common_other_space.value + "").replace(",", ".")) * parseFloat((this.dependantSettings.price_points_settings.common_indoor_shared_with_multiple_addresses.common_other_space_points+'').replace(",", "."))) / (addresses * units);
          Vue.set(this.indoor.common_other_space, "total", p);
          sum += p;
        }
        Vue.set(this.indoor, "total", this.roundToQuarter(sum));
        this.countDependantTotal();
      },
      countSharedParkingPoints() {
        var points = 0;
        var addresses = Math.max(1, parseFloat((this.sharedParking.number_of_addresses+'').replace(",", ".")) || 1);
        var is2026    = (this.dependantSettings.up_to_date || '') >= '2026-01-01';
        var divisor   = is2026
          ? addresses * Math.max(1, parseFloat((this.sharedParking.number_of_residents+'').replace(",", ".")) || 1)
          : addresses;
        if(this.sharedParking.type != 0 && this.sharedParking.type !== null){
          var typePoints = parseFloat((this.dependantSettings.price_points_settings.shared_parking[this.sharedParking.type]+'' || 0).replace(',', '.'));
          if(!isNaN(typePoints)){ points = typePoints / divisor; }
        }
        Vue.set(this.sharedParking, "total", this.roundToQuarter(points));
        this.countDependantTotal();
      },
      countVopPoints() {
        var price = parseFloat((this.vop.price.value+'').replace(',', '.')) || 0;
        var surface = Math.round(parseFloat((this.vop.surface+'').replace(',', '.')) || 0);
        var averagePrice = parseFloat((this.vop.average_price+'').replace(',', '.')) || 0;
        var valuation = parseFloat(((this.vop.valuation && this.vop.valuation.value)+'').replace(',', '.')) || 0;
        var lowerTierPts = parseFloat((this.dependantSettings.price_points_settings.vop.lower_than_10+'').replace(',', '.'));
        var fallbackPct = (parseFloat((this.dependantSettings.price_points_settings.vop.valuation_fallback_percentage+'').replace(',', '.')) || 85) / 100;

        // Official rule: WOZ value takes priority; if absent, 85% of an accredited
        // Register-Taxateur valuation may be used; if neither is known, the lowest
        // tier applies (never 0) — never estimate by comparison to neighbours.
        var effectivePrice = price;
        var fallbackUsed = 'woz';
        if (effectivePrice <= 0) {
          if (valuation > 0) {
            effectivePrice = valuation * fallbackPct;
            fallbackUsed = 'valuation';
          } else {
            fallbackUsed = 'none';
          }
        }

        var points;
        var pointsPerSurface = 0;
        var differencePercent = 0;
        var category = 'none';
        var hasData = effectivePrice > 0 && surface > 0 && averagePrice > 0;
        if (!hasData) {
          points = lowerTierPts;
        } else {
          pointsPerSurface = effectivePrice / surface;
          differencePercent = ((pointsPerSurface - averagePrice) / averagePrice) * 100;
          var lower = 0.9 * averagePrice;
          var higher = 1.1 * averagePrice;
          if (pointsPerSurface > higher) {
            points = parseFloat((this.dependantSettings.price_points_settings.vop.higher_than_10+'').replace(',', '.'));
            category = 'above';
          } else if (pointsPerSurface >= lower) {
            points = parseFloat((this.dependantSettings.price_points_settings.vop.not_lower_than_10_and_not_higher_than_10+'').replace(',', '.'));
            category = 'within';
          } else {
            points = lowerTierPts;
            category = 'below';
          }
        }
        var total = this.roundToQuarter(points);
        Vue.set(this.vop, "total", total);
        Vue.set(this.vop, "hasData", hasData);
        Vue.set(this.vop, "priceUsed", effectivePrice);
        Vue.set(this.vop, "surfaceUsed", surface);
        Vue.set(this.vop, "averagePriceUsed", averagePrice);
        Vue.set(this.vop, "pointsPerSurface", pointsPerSurface);
        Vue.set(this.vop, "differencePercent", differencePercent);
        Vue.set(this.vop, "category", category);
        Vue.set(this.vop, "fallbackUsed", fallbackUsed);

        this.countDependantTotal();
      },
      countExtraPoints() {
        var sum = 0;
        if (
          this.extraPoints.care_home &&
          this.extraPoints.care_home == 1
        ) {
          // Care home (Rubriek 12): +35% of rubrics 1–11 total, rounded to 0.25
          var rubrics1to11 =
            parseFloat(this.leaveArea.total+"") +
            parseFloat(this.otherSpaces.total+"") +
            parseFloat(this.heating.total+"") +
            parseFloat(this.energy.total+"") +
            parseFloat(this.kitchen.total+"") +
            parseFloat(this.sanitary.total+"") +
            parseFloat(this.disabled.total+"") +
            parseFloat(this.outdoor.total+"") +
            parseFloat(this.indoor.total+"") +
            parseFloat(this.sharedParking.total+"") +
            parseFloat(this.vop.total+"");
          var careHomeBonus = rubrics1to11 * (parseFloat((this.dependantSettings.price_points_settings.care_home.points+'').replace(",", ".")) / 100);
          sum += this.roundToQuarter(careHomeBonus);
        }
        if (this.extraPoints.has_automatic_bell && this.extraPoints.has_automatic_bell == 1) {
          sum += parseFloat((this.dependantSettings.price_points_settings.care_home.automatic_bell_points+'').replace(",", "."));
        }
        if (this.extraPoints.has_charging_station && this.extraPoints.has_charging_station == 1) {
          sum += parseFloat((this.dependantSettings.price_points_settings.care_home.charging_station+'').replace(",", "."));
        }
        Vue.set(this.extraPoints, "total", this.roundToQuarter(sum));
        // Compute price from rubrics 1-12 before applying monument surcharge
        this.countDependantTotal();
        if (this.extraPoints.national_monument && this.extraPoints.national_monument.value) {
          // Parse the price (stored as Dutch comma-decimal string) before arithmetic.
          var basePrice = parseFloat((this.price + '').replace(',', '.')) || 0;
          switch(this.extraPoints.national_monument.value){
            case '0':
              // Municipal/provincial monument: +15% of max rent price
              this.price = Math.round(basePrice * (1 + parseFloat((this.dependantSettings.price_points_settings.monument.municipal_or_provincial_monument_percentage+'').replace(",", ".")) / 100) * 100) / 100;
              break;
            case '1':
              // Protected townscape/village view: +5% of max rent price
              this.price = Math.round(basePrice * (1 + parseFloat((this.dependantSettings.price_points_settings.monument.protected_town_and_village_view_percentage+'').replace(",", ".")) / 100) * 100) / 100;
              break;
            case '2':
              // National monument pre-2024 (legacy): fixed point bonus, no percentage
              sum += parseFloat((this.dependantSettings.price_points_settings.monument.national_monument_points+'').replace(",", ".") || 10);
              Vue.set(this.extraPoints, "total", this.roundToQuarter(sum));
              this.countDependantTotal();
              break;
            case '3':
              // National monument post-2024: +35% of max rent price
              this.price = Math.round(basePrice * (1 + parseFloat((this.dependantSettings.price_points_settings.monument.national_monument_percentage+'').replace(",", ".")) / 100) * 100) / 100;
              break;
          }
        }
      },
      countDeductionPoints() {
        var sum = 0;

        // Small private room (<8 m²): -4 pts per official Rubriek 13
        if (this.surface > 0 && this.surface < 8) {
          sum -= parseFloat((this.dependantSettings.price_points_settings.deduction.small_area_negative_points + "").replace(",", ".") || 4);
        }
        for (var ob in this.deductionOptions) {
          if (ob != "total") {
            if (this.deductionOptions[ob].value && parseFloat(this.deductionOptions[ob].value + "")) {
              var pts = parseFloat((this.deductionOptions[ob].points + "").replace(",", "."));
              sum -= pts;
              Vue.set(this.deductionOptions[ob], "total", -pts);
            } else {
              Vue.set(this.deductionOptions[ob], "total", 0);
            }
          }
        }
        Vue.set(this.deductionOptions, "total", sum);
        this.countDependantTotal();
      },
      countDependantTotal() {
          var total =
          parseFloat(this.leaveArea.total+"") +
          parseFloat(this.otherSpaces.total+"") +
          parseFloat(this.heating.total+"") +
          parseFloat(this.energy.total+"") +
          parseFloat(this.kitchen.total+"") +
          parseFloat(this.sanitary.total+"") +
          parseFloat(this.disabled.total+"") +
          parseFloat(this.outdoor.total+"") +
          parseFloat(this.indoor.total+"") +
          parseFloat(this.sharedParking.total+"") +
          parseFloat(this.vop.total+"") +
          parseFloat(this.extraPoints.total+"") +
          parseFloat(this.deductionOptions.total+"");
        this.total = Math.round(total);
        this.totalPoints = this.total;
        var p = this.totalPoints < 750 ? this.totalPoints : 750;
        this.price = this.dependantMaxRentalPrices[p];
      },
      formatPts(val) {
        if (val === null || val === undefined || val === '') return '';
        var n = parseFloat(String(val).replace(',', '.'));
        if (isNaN(n)) return '';
        if (n % 1 === 0) return String(Math.round(n));
        return String(n).replace('.', ',');
      },
      /**
       * Renders a fixed point value as "(+2 points)" / "(-4 points)" /
       * "(0 points)" / "(+0,65 points per m²)" - see IndependantFormNew.vue's
       * identical method for the full rationale (this codebase duplicates
       * formatPts() the same way rather than sharing a mixin).
       */
      pointsLabel(val, opts) {
        opts = opts || {};
        if (val === null || val === undefined || val === '') return '';
        var n = parseFloat(String(val).replace(',', '.'));
        if (isNaN(n)) return '';
        var formatted = this.formatPts(n);
        var signed = n > 0 ? ('+' + formatted) : formatted;
        var word = Math.abs(n) === 1
          ? ((this.locales && this.locales.properties && this.locales.properties.point) || 'point')
          : ((this.locales && this.locales.properties && this.locales.properties.points) || 'points');
        var text = signed + ' ' + word;
        if (opts.suffix) {
          text += ' ' + opts.suffix;
        }
        return opts.wrap === false ? text : '(' + text + ')';
      },
      /** Renders a percentage rule as "(+15%)" - for surcharges on rent price, not points. */
      percentLabel(val, opts) {
        opts = opts || {};
        if (val === null || val === undefined || val === '') return '';
        var n = parseFloat(String(val).replace(',', '.'));
        if (isNaN(n)) return '';
        var formatted = this.formatPts(n);
        var signed = n > 0 ? ('+' + formatted) : formatted;
        var text = signed + '%';
        if (opts.suffix) {
          text += ' ' + opts.suffix;
        }
        return opts.wrap === false ? text : '(' + text + ')';
      },

      totalLabel(text) {
        let addText = this.locales && this.locales.common && this.locales.common.total;
        if (text && addText) {
          return `${text} ${addText}`;
        }
        if (text) {
          return text;
        }
        if (addText) {
          return addText;
        }
        return '---';
      },
    },
  };
</script>